import { useState, useEffect, useCallback } from 'react'
import { db } from '../firebase'
import {
  collection,
  getDocs,
  addDoc,
  setDoc,
  doc,
  query,
  where,
  runTransaction
} from 'firebase/firestore'

const sortByName = (list) => [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''))

export function usePeople(user, familyId) {
  const [people, setPeople] = useState([])
  const [loading, setLoading] = useState(true)

  const getPeopleRef = useCallback(() => {
    // Use family-specific collection
    return user && familyId ? collection(db, 'familyTree', familyId, 'people') : null
  }, [user, familyId])

  const fetchPeople = useCallback(async () => {
    const ref = getPeopleRef()
    if (!ref) {
      setPeople([])
      setLoading(false)
      return
    }
    try {
      const snap = await getDocs(ref)
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      data.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      setPeople(data)
    } catch (err) {
      console.error('Error fetching people:', err)
    }
    setLoading(false)
  }, [getPeopleRef])

  useEffect(() => {
    fetchPeople()
  }, [fetchPeople])

  const addPerson = async (personData) => {
    const ref = getPeopleRef()
    if (!ref) return null

    // Initialize relationship arrays if not provided
    const dataWithRelations = {
      ...personData,
      spouses: personData.spouses || [],
      children: personData.children || [],
      parents: personData.parents || []
    }

    const docRef = await addDoc(ref, dataWithRelations)
    setPeople(prev => sortByName([...prev, { id: docRef.id, ...dataWithRelations }]))
    return docRef.id
  }

  const updatePerson = async (personId, personData) => {
    if (!user || !familyId) return
    await setDoc(doc(db, 'familyTree', familyId, 'people', personId), personData, { merge: true })
    setPeople(prev => sortByName(prev.map(p => p.id === personId ? { ...p, ...personData } : p)))
  }

  const deletePerson = async (personId) => {
    if (!user || !familyId) return

    // Read current person data from local state for optimistic update
    const person = people.find(p => p.id === personId)
    const spouses = person?.spouses || []
    const children = person?.children || []
    const parents = person?.parents || []

    await runTransaction(db, async (transaction) => {
      const personRef = doc(db, 'familyTree', familyId, 'people', personId)
      const personDoc = await transaction.get(personRef)

      if (!personDoc.exists()) {
        throw new Error('Person not found')
      }

      const personData = personDoc.data()
      const txSpouses = personData.spouses || []
      const txChildren = personData.children || []
      const txParents = personData.parents || []

      // Remove this person from all their spouses
      for (const spouseId of txSpouses) {
        const spouseRef = doc(db, 'familyTree', familyId, 'people', spouseId)
        const spouseDoc = await transaction.get(spouseRef)
        if (spouseDoc.exists()) {
          const spouseData = spouseDoc.data()
          transaction.update(spouseRef, {
            spouses: (spouseData.spouses || []).filter(id => id !== personId),
            children: (spouseData.children || []).filter(id => !txChildren.includes(id))
          })
        }
      }

      // Remove this person from all their children's parent lists
      for (const childId of txChildren) {
        const childRef = doc(db, 'familyTree', familyId, 'people', childId)
        const childDoc = await transaction.get(childRef)
        if (childDoc.exists()) {
          const childData = childDoc.data()
          transaction.update(childRef, {
            parents: (childData.parents || []).filter(id => id !== personId)
          })
        }
      }

      // Remove this person from all their parents' children lists
      for (const parentId of txParents) {
        const parentRef = doc(db, 'familyTree', familyId, 'people', parentId)
        const parentDoc = await transaction.get(parentRef)
        if (parentDoc.exists()) {
          const parentData = parentDoc.data()
          transaction.update(parentRef, {
            children: (parentData.children || []).filter(id => id !== personId)
          })
        }
      }

      // Finally, delete the person
      transaction.delete(personRef)
    })

    // Optimistic update: remove person and strip refs from related people
    setPeople(prev => sortByName(
      prev
        .filter(p => p.id !== personId)
        .map(p => {
          let updated = p
          if (spouses.includes(p.id)) {
            updated = {
              ...updated,
              spouses: (updated.spouses || []).filter(id => id !== personId),
              children: (updated.children || []).filter(id => !children.includes(id))
            }
          }
          if (children.includes(p.id)) {
            updated = { ...updated, parents: (updated.parents || []).filter(id => id !== personId) }
          }
          if (parents.includes(p.id)) {
            updated = { ...updated, children: (updated.children || []).filter(id => id !== personId) }
          }
          return updated
        })
    ))
  }

  const getOrCreateByName = async (name) => {
    const ref = getPeopleRef()
    if (!ref) return null
    const q = query(ref, where('name', '==', name))
    const snap = await getDocs(q)
    if (!snap.empty) return snap.docs[0].id
    const newData = { name, spouses: [], children: [], parents: [] }
    const docRef = await addDoc(ref, newData)
    setPeople(prev => sortByName([...prev, { id: docRef.id, ...newData }]))
    return docRef.id
  }

  const isNameTaken = async (name, excludeId = null) => {
    const ref = getPeopleRef()
    if (!ref) return false
    const q = query(ref, where('name', '==', name))
    const snap = await getDocs(q)
    if (snap.empty) return false
    if (excludeId && snap.docs.length === 1 && snap.docs[0].id === excludeId) return false
    return true
  }

  // ===== Relationship Management with Transactions =====

  const addSpouse = async (personId, spouseId) => {
    if (!user || !familyId || personId === spouseId) return

    await runTransaction(db, async (transaction) => {
      const personRef = doc(db, 'familyTree', familyId, 'people', personId)
      const spouseRef = doc(db, 'familyTree', familyId, 'people', spouseId)

      const personDoc = await transaction.get(personRef)
      const spouseDoc = await transaction.get(spouseRef)

      if (!personDoc.exists() || !spouseDoc.exists()) {
        throw new Error('Person or spouse not found')
      }

      const personData = personDoc.data()
      const spouseData = spouseDoc.data()

      const personSpouses = personData.spouses || []
      const spouseSpouses = spouseData.spouses || []

      if (!personSpouses.includes(spouseId)) {
        transaction.update(personRef, {
          spouses: [...personSpouses, spouseId]
        })
      }

      if (!spouseSpouses.includes(personId)) {
        transaction.update(spouseRef, {
          spouses: [...spouseSpouses, personId]
        })
      }
    })

    setPeople(prev => sortByName(prev.map(p => {
      if (p.id === personId && !(p.spouses || []).includes(spouseId)) {
        return { ...p, spouses: [...(p.spouses || []), spouseId] }
      }
      if (p.id === spouseId && !(p.spouses || []).includes(personId)) {
        return { ...p, spouses: [...(p.spouses || []), personId] }
      }
      return p
    })))
  }

  const removeSpouse = async (personId, spouseId) => {
    if (!user || !familyId) return

    await runTransaction(db, async (transaction) => {
      const personRef = doc(db, 'familyTree', familyId, 'people', personId)
      const spouseRef = doc(db, 'familyTree', familyId, 'people', spouseId)

      const personDoc = await transaction.get(personRef)
      const spouseDoc = await transaction.get(spouseRef)

      if (!personDoc.exists() || !spouseDoc.exists()) {
        throw new Error('Person or spouse not found')
      }

      const personData = personDoc.data()
      const spouseData = spouseDoc.data()

      transaction.update(personRef, {
        spouses: (personData.spouses || []).filter(id => id !== spouseId)
      })

      transaction.update(spouseRef, {
        spouses: (spouseData.spouses || []).filter(id => id !== personId)
      })
    })

    setPeople(prev => sortByName(prev.map(p => {
      if (p.id === personId) {
        return { ...p, spouses: (p.spouses || []).filter(id => id !== spouseId) }
      }
      if (p.id === spouseId) {
        return { ...p, spouses: (p.spouses || []).filter(id => id !== personId) }
      }
      return p
    })))
  }

  const addChild = async (parentId, childId) => {
    if (!user || !familyId || parentId === childId) return

    await runTransaction(db, async (transaction) => {
      const parentRef = doc(db, 'familyTree', familyId, 'people', parentId)
      const childRef = doc(db, 'familyTree', familyId, 'people', childId)

      const parentDoc = await transaction.get(parentRef)
      const childDoc = await transaction.get(childRef)

      if (!parentDoc.exists() || !childDoc.exists()) {
        throw new Error('Parent or child not found')
      }

      const parentData = parentDoc.data()
      const childData = childDoc.data()

      const parentChildren = parentData.children || []
      const childParents = childData.parents || []

      if (!parentChildren.includes(childId)) {
        transaction.update(parentRef, {
          children: [...parentChildren, childId]
        })
      }

      if (!childParents.includes(parentId)) {
        transaction.update(childRef, {
          parents: [...childParents, parentId]
        })
      }
    })

    setPeople(prev => sortByName(prev.map(p => {
      if (p.id === parentId && !(p.children || []).includes(childId)) {
        return { ...p, children: [...(p.children || []), childId] }
      }
      if (p.id === childId && !(p.parents || []).includes(parentId)) {
        return { ...p, parents: [...(p.parents || []), parentId] }
      }
      return p
    })))
  }

  const removeChild = async (parentId, childId) => {
    if (!user || !familyId) return

    await runTransaction(db, async (transaction) => {
      const parentRef = doc(db, 'familyTree', familyId, 'people', parentId)
      const childRef = doc(db, 'familyTree', familyId, 'people', childId)

      const parentDoc = await transaction.get(parentRef)
      const childDoc = await transaction.get(childRef)

      if (!parentDoc.exists() || !childDoc.exists()) {
        throw new Error('Parent or child not found')
      }

      const parentData = parentDoc.data()
      const childData = childDoc.data()

      transaction.update(parentRef, {
        children: (parentData.children || []).filter(id => id !== childId)
      })

      transaction.update(childRef, {
        parents: (childData.parents || []).filter(id => id !== parentId)
      })
    })

    setPeople(prev => sortByName(prev.map(p => {
      if (p.id === parentId) {
        return { ...p, children: (p.children || []).filter(id => id !== childId) }
      }
      if (p.id === childId) {
        return { ...p, parents: (p.parents || []).filter(id => id !== parentId) }
      }
      return p
    })))
  }

  const addParent = async (childId, parentId) => {
    // Same as addChild but with reversed parameter order for clarity
    return addChild(parentId, childId)
  }

  const createFamily = async (spouse1Id, spouse2Id, childrenIds = []) => {
    if (!user || !familyId) return

    await runTransaction(db, async (transaction) => {
      // Get all document references
      const spouse1Ref = doc(db, 'familyTree', familyId, 'people', spouse1Id)
      const spouse2Ref = doc(db, 'familyTree', familyId, 'people', spouse2Id)

      // Read spouse documents
      const spouse1Doc = await transaction.get(spouse1Ref)
      const spouse2Doc = await transaction.get(spouse2Ref)

      if (!spouse1Doc.exists() || !spouse2Doc.exists()) {
        throw new Error('One or both spouses not found')
      }

      const spouse1Data = spouse1Doc.data()
      const spouse2Data = spouse2Doc.data()

      // Add spouses to each other
      const spouse1Spouses = spouse1Data.spouses || []
      const spouse2Spouses = spouse2Data.spouses || []

      if (!spouse1Spouses.includes(spouse2Id)) {
        spouse1Spouses.push(spouse2Id)
      }
      if (!spouse2Spouses.includes(spouse1Id)) {
        spouse2Spouses.push(spouse1Id)
      }

      // Process children
      const spouse1Children = spouse1Data.children || []
      const spouse2Children = spouse2Data.children || []

      const childUpdates = {} // childId -> new parents array

      for (const childId of childrenIds) {
        const childRef = doc(db, 'familyTree', familyId, 'people', childId)
        const childDoc = await transaction.get(childRef)

        if (childDoc.exists()) {
          const childData = childDoc.data()
          const childParents = childData.parents || []

          // Add child to both parents
          if (!spouse1Children.includes(childId)) {
            spouse1Children.push(childId)
          }
          if (!spouse2Children.includes(childId)) {
            spouse2Children.push(childId)
          }

          // Add both parents to child
          if (!childParents.includes(spouse1Id)) {
            childParents.push(spouse1Id)
          }
          if (!childParents.includes(spouse2Id)) {
            childParents.push(spouse2Id)
          }

          childUpdates[childId] = childParents
          transaction.update(childRef, { parents: childParents })
        }
      }

      // Update both spouses
      transaction.update(spouse1Ref, {
        spouses: spouse1Spouses,
        children: spouse1Children
      })

      transaction.update(spouse2Ref, {
        spouses: spouse2Spouses,
        children: spouse2Children
      })
    })

    // Optimistic local update
    setPeople(prev => sortByName(prev.map(p => {
      if (p.id === spouse1Id) {
        const spouses = (p.spouses || []).includes(spouse2Id) ? p.spouses : [...(p.spouses || []), spouse2Id]
        const children = [...new Set([...(p.children || []), ...childrenIds])]
        return { ...p, spouses, children }
      }
      if (p.id === spouse2Id) {
        const spouses = (p.spouses || []).includes(spouse1Id) ? p.spouses : [...(p.spouses || []), spouse1Id]
        const children = [...new Set([...(p.children || []), ...childrenIds])]
        return { ...p, spouses, children }
      }
      if (childrenIds.includes(p.id)) {
        const parents = [...new Set([...(p.parents || []), spouse1Id, spouse2Id])]
        return { ...p, parents }
      }
      return p
    })))
  }

  return {
    people,
    loading,
    addPerson,
    updatePerson,
    deletePerson,
    getOrCreateByName,
    isNameTaken,
    refetch: fetchPeople,
    // Relationship management functions
    addSpouse,
    removeSpouse,
    addChild,
    removeChild,
    addParent,
    createFamily
  }
}
